/**
 * The print-friendly recovery card, and the QR code it carries.
 *
 * Shared by the three places a card is produced: immediately after a profile is
 * created, after its credentials are replaced, and from the dashboard when an
 * owner types a phrase back in to reprint. One module so the three cannot drift
 * — a card that disagrees with another card is a support problem nobody can
 * diagnose from the outside.
 *
 * The card carries the KinCode domain, the profile label, the phrase and a QR
 * code, and deliberately **not the PIN**. It is the artefact most likely to be
 * left in a school bag or a coat pocket, so on its own it must not be enough to
 * retrieve anybody's contacts.
 *
 * Hidden on screen and revealed only by the print stylesheet, which also hides
 * the rest of the page — so the browser's own print action produces the card
 * rather than a screenshot of the whole interface including the PIN.
 */

import { el } from "./dom.js";
import { KINCODE_DOMAIN, retrievalUrl } from "./phrase.js";

/**
 * A QR code encoding the retrieval URL for a phrase.
 *
 * `label` is set explicitly because `wa-qr-code` otherwise falls back to using
 * its `value` as the accessible name, which would have a screen reader read out
 * the whole URL — phrase included — to a room.
 *
 * @param {string} phrase
 * @param {{ size?: number, className?: string }} [options]
 * @returns {HTMLElement}
 */
export function renderQrCode(phrase, options = {}) {
  return el("wa-qr-code", {
    value: retrievalUrl(phrase),
    label: "QR code that opens the retrieval page with this recovery phrase filled in",
    // Drawn at twice this in device pixels by the component, so 200 gives a
    // 400px image — enough to stay sharp at the ~40mm the print stylesheet
    // renders it at, which is around 250dpi.
    size: options.size ?? 200,
    class: options.className ?? null,
  });
}

/**
 * Wait until every QR code under `root` has drawn itself.
 *
 * `wa-qr-code` paints its canvas from Lit's `updated()` callback, which is
 * asynchronous. A flow that appends a card and calls `window.print()` in the
 * same task prints an empty box. Anything printing programmatically must await
 * this first; a card the user prints by clicking a button minutes later is
 * already settled and does not need it.
 *
 * @param {ParentNode} root
 * @returns {Promise<void>}
 */
export async function whenQrReady(root) {
  const codes = [...root.querySelectorAll("wa-qr-code")];
  await Promise.all(
    codes.map((code) => {
      const pending = /** @type {{ updateComplete?: Promise<unknown> }} */ (
        /** @type {unknown} */ (code)
      ).updateComplete;
      return pending ?? Promise.resolve();
    }),
  );
}

/**
 * Build the printable card for a profile.
 *
 * @param {{ label: string, phrase: string }} profile
 * @returns {HTMLElement}
 */
export function renderPrintCard({ label, phrase }) {
  return el("section", { class: "kin-print-card", "aria-hidden": "true" }, [
    el("h2", {}, ["KinCode"]),
    el("p", { class: "kin-print-card__domain" }, [KINCODE_DOMAIN]),
    el("div", { class: "kin-print-card__body" }, [
      el("dl", {}, [
        el("dt", {}, ["Profile"]),
        el("dd", {}, [label]),
        el("dt", {}, ["Recovery phrase"]),
        el("dd", { class: "kin-print-card__phrase" }, [phrase]),
      ]),
      renderQrCode(phrase, { className: "kin-print-card__qr" }),
    ]),
    el("ol", {}, [
      el("li", {}, [`Scan the code above, or go to ${KINCODE_DOMAIN} on any phone or computer.`]),
      el("li", {}, ["Scanning fills in the recovery phrase. Otherwise type it in yourself."]),
      el("li", {}, ["Enter the PIN you were given separately. It is not on this card."]),
      el("li", {}, ["The contact numbers will be shown."]),
    ]),
    el("p", { class: "kin-print-card__notice" }, [
      "In an emergency always call 999. KinCode is not an emergency service.",
    ]),
  ]);
}

/**
 * Print a card for a phrase that is not already on the page.
 *
 * Used by the dashboard reprint flow, where the owner has typed the phrase back
 * in. The card is injected, printed and removed again. Removing it matters twice
 * over: a leftover `.kin-print-card` makes the *next* print of that page produce
 * the stale card instead of the page, and it would leave the typed phrase
 * sitting in the document afterwards.
 *
 * @param {{ label: string, phrase: string }} profile
 * @returns {Promise<void>}
 */
export async function printCardFor(profile) {
  // There must never be two cards in the document: the print stylesheet reveals
  // every `.kin-print-card` it finds, so a second one would print as well.
  for (const stale of document.querySelectorAll(".kin-print-card")) {
    stale.remove();
  }

  const card = renderPrintCard(profile);
  document.body.append(card);

  // Without this the canvas has not been painted yet and the card prints with an
  // empty box where the QR code should be.
  await whenQrReady(card);

  // `afterprint` is the removal trigger rather than the return of `print()`.
  // `print()` blocks until the dialog closes in every browser that matters, but
  // where it does not, removing the card on the next line would pull it out from
  // under the printer and produce a blank sheet — the worse of the two failures.
  // The timer only exists for a browser that never fires the event at all.
  const cleanUp = () => card.remove();
  window.addEventListener("afterprint", cleanUp, { once: true });
  globalThis.setTimeout(cleanUp, 60000);
  window.print();
}
